import React from "react";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { AlertTriangle, Crown, Map, Layers } from "lucide-react";

const LimitsContainer = styled.div`
  background: var(--surface-color);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 1rem;
`;

const LimitsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  font-weight: 600;
`;

const LimitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const LimitItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: var(--background-color);
  border-radius: 6px;
  border-left: 3px solid var(--primary-color);
`;

const LimitLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
`;

const LimitValue = styled.div<{ $isLimited: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.$isLimited ? "var(--error-color)" : "var(--success-color)"};
`;

const UpgradePrompt = styled.div`
  background: linear-gradient(135deg, var(--primary-color) 0%, #764ba2 100%);
  color: white;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const UpgradeButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  margin-left: auto;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const WarningMessage = styled.div`
  background: var(--warning-bg);
  color: var(--warning-text);
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid var(--warning-border);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

interface UserLimitsDisplayProps {
  showUpgradePrompt?: boolean;
}

const UserLimitsDisplay: React.FC<UserLimitsDisplayProps> = ({
  showUpgradePrompt = false, // Default to false - don't show upgrade prompts
}) => {
  const {
    user,
    getRemainingMapSlots,
    getRemainingOverlaySlots,
    canCreateMap,
    canCreateOverlay,
  } = useAuth();

  if (!user) return null;

  const remainingMaps = getRemainingMapSlots();
  const remainingOverlays = getRemainingOverlaySlots();
  const canCreateNewMap = canCreateMap();
  const canCreateNewOverlay = canCreateOverlay();

  const isLimited = !canCreateNewMap || !canCreateNewOverlay;
  const isFreeUser = user.subscriptionTier === "free";
  
  // Only show limits when user has 50+ maps (approaching the limit)
  const shouldShowLimits = user.mapsCount >= 50;

  // Don't render anything if user is below the threshold
  if (!shouldShowLimits && !isLimited) {
    return null;
  }

  return (
    <LimitsContainer>
      <LimitsHeader>
        <Crown size={20} />
        Your Account Limits
      </LimitsHeader>

      <LimitsGrid>
        <LimitItem>
          <LimitLabel>
            <Map size={16} />
            Maps Created
          </LimitLabel>
          <LimitValue $isLimited={!canCreateNewMap}>
            {user.mapsCount} /{" "}
            {remainingMaps === -1 ? "∞" : user.mapsCount + remainingMaps}
          </LimitValue>
        </LimitItem>

        <LimitItem>
          <LimitLabel>
            <Layers size={16} />
            Overlays Created
          </LimitLabel>
          <LimitValue $isLimited={!canCreateNewOverlay}>
            {user.mapsCount} /{" "}
            {remainingOverlays === -1
              ? "∞"
              : user.mapsCount + remainingOverlays}
          </LimitValue>
        </LimitItem>
      </LimitsGrid>

      {isLimited && (
        <WarningMessage>
          <AlertTriangle size={16} />
          You've reached your limit for {!canCreateNewMap ? "maps" : "overlays"}
          . Contact support for assistance.
        </WarningMessage>
      )}

      {/* Upgrade prompt is now hidden by default */}
      {false && showUpgradePrompt && isFreeUser && (
        <UpgradePrompt>
          <Crown size={20} />
          <div>
            <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>
              Upgrade to Premium
            </div>
            <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
              Get unlimited maps and overlays, plus advanced features
            </div>
          </div>
          <UpgradeButton
            onClick={() => {
              // TODO: Implement upgrade flow
              alert("Upgrade feature coming soon!");
            }}
          >
            Upgrade Now
          </UpgradeButton>
        </UpgradePrompt>
      )}
    </LimitsContainer>
  );
};

export default UserLimitsDisplay;
